import type { ReviewKind } from '../../../shared/progress';
import type { SceneVars } from '../../lib/tourScenes';
import Eyebrow from '../Eyebrow';
import ReviewPageChrome, {
  BRIEF_AUDIENCE_BODY,
  BRIEF_AUDIENCE_TITLE,
  BRIEF_PURPOSE_BODY,
  BRIEF_PURPOSE_TITLE,
  BriefDocPanel,
  DocPanel,
} from './PageChrome';

/**
 * general-notes stage — review card page (A) soft-fades (opacity only, no
 * teal/paper overlay treatment) to a notes panel (B) modelled on the real
 * ClientNotesPanel: eyebrow, save indicator, textarea. Only the document on
 * page A and one word of page B's helper line change on an interview brief;
 * the panel itself is the same on both kinds.
 */
export default function GeneralNotesStage({ vars, kind }: { vars: SceneVars; kind: ReviewKind }) {
  const page = vars.page as string;
  const typed = (vars.typed as string) ?? '';
  const saveState = vars.saveState as 'idle' | 'saving' | 'saved';
  const brief = kind === 'brief';

  return (
    <>
      <Page visible={page === 'A'}>
        <ReviewPageChrome kind={kind} card={{ notesLit: Boolean(vars.notesActionLit) }}>
          {brief ? (
            <BriefDocPanel
              top={345}
              sections={[
                { title: BRIEF_PURPOSE_TITLE, paragraphs: [BRIEF_PURPOSE_BODY] },
                { title: BRIEF_AUDIENCE_TITLE, paragraphs: [BRIEF_AUDIENCE_BODY] },
              ]}
            />
          ) : (
            <DocPanel
              top={345}
              rows={[
                {
                  audio: 'Every morning starts the same way: the first pour.',
                  visual: 'Steam over the machine, dawn light.',
                },
                {
                  audio: 'Aurora roasts in small batches every week.',
                  visual: 'Hands tipping green beans into the roaster.',
                },
              ]}
            />
          )}
        </ReviewPageChrome>
      </Page>

      <Page visible={page === 'B'}>
        <div className="absolute inset-0 bg-page flex items-center justify-center px-9">
          <div className="w-full max-w-[720px] bg-elevated border border-line rounded-md p-7">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Eyebrow accent>// Client notes</Eyebrow>
                <p className="text-faint text-[12px] mt-1.5">
                  Free-form summary, shared with everyone reviewing this {brief ? 'brief' : 'script'}.
                </p>
              </div>
              <SaveChip state={saveState} />
            </div>
            <div
              data-tour-target="notes-input"
              className="w-full min-h-[140px] px-4 py-3 bg-card border border-line rounded-sm text-fg text-[15px] leading-[1.55]"
            >
              {typed || <span className="text-faint">Overall thoughts, tone direction, things you'd like to change…</span>}
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function Page({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      className={`absolute inset-0 transition-opacity duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {children}
    </div>
  );
}

function SaveChip({ state }: { state: 'idle' | 'saving' | 'saved' }) {
  if (state === 'idle') return null;
  const saved = state === 'saved';
  return (
    <span
      className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] font-semibold ${
        saved ? 'text-success' : 'text-muted'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${saved ? 'bg-success' : 'bg-accent animate-pulse'}`} aria-hidden />
      {saved ? 'Saved' : 'Saving'}
    </span>
  );
}
